import React, { Fragment, useState } from 'react';

import { ExpandMoreButton } from '@components/ExpandMoreButton';
import {
  BookingInquiryCargosDisplay,
  type BookingInquiryCargosDisplayProps,
} from '@components/utilities/BookingInquiry/BookingInquiryCargosDisplay';
import { BookingInquiryContactDisplay } from '@components/utilities/BookingInquiry/BookingInquiryContact';
import { PriceSettlement2 } from '@components/utilities/BookingInquiry/PriceSettlement2';
import { RoutePlanLineDisplay } from '@components/utilities/BookingInquiry/RoutePlanLineDisplay';
import { bookingInquiryStrings, bookingInquiryTradeConditionStrings } from '@data/bookingInquiry';
import { instructionStrings } from '@data/instruction';
import { routePlanServiceNameStrings, routePlanStrings } from '@data/routePlan';
import { tradingAgencyStrings } from '@data/tradingAgency';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { BaseInquiryDetail, BookingInquiryAdminGroupNames } from '@models/BookingInquiry';
import { countryStrings } from '@models/Country';
import { BookingInquiryHelper } from '@models/next/BookingInquiryHelper';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';
import { sumBy } from 'remeda';

const useStyles = makeMuiStyles((theme, common) => ({
  bookingInquiryDisplayContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 8,
    '& > div': {
      position: 'relative',
      backgroundColor: theme.palette.background.paper,
      padding: '16px 16px',
    },
    '&.content > div': {
      borderBottom: `1px dashed ${Color(theme.palette.primary.main).alpha(0.2).toString()}`,
    },
  },
  bookingInquiryDisplayTitle: {
    ...common.middleText,
    ...common.textGray900Important,
    ...common.fontWeight600,
    marginBottom: '0.5rem',
  },
  routePlanCardServiceName: {
    ...common.flexCenter,
    ...common.textPrimary,
    ...common.exSmallerText,
    ...common.selfStart,
    ...common.fontWeight600,
    height: 20,
    padding: '0 14px',
    marginLeft: '1rem',
    borderRadius: 2,
    backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
  },
  routePlanLineDisplayContainer: {
    paddingTop: 12,
    paddingBottom: 16,
    paddingLeft: 30,
    paddingRight: 30,
    '.content &': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  cargoGroupContainer: {
    ...common.smallerText,
    ...common.textGray900,
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      columnGap: 0,
    },
    columnGap: '5.25rem',
    rowGap: '1rem',
  },
  cargoGroupTitle: {
    ...common.flexColumn,
    ...common.justifyCenter,
  },
  cargoGroupList: {
    display: 'grid',
    columnGap: 30,
    paddingLeft: 50,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      paddingLeft: 0,
    },
    '& > div': {
      maxWidth: 100,
      ...common.preEllipsis,
    },
    '&$cargoGroupLWH': {
      gridTemplateColumns: 'auto auto auto auto',
    },
    '&$cargoGroupWV': {
      gridTemplateColumns: 'auto auto auto',
    },
  },
  cargoGroupLWH: {},
  cargoGroupWV: {},
  expandMoreContainer: {
    ...common.flexColumnCenter,
    ...common.textPrimary,
    ...common.baseButton,
    padding: '10px 0',
    gridColumn: 'span 2',
  },
  bookingInquiryDisplayGapDivider: {
    position: 'absolute',
    top: 20,
    bottom: 20,
    left: -4,
    width: 1,
    borderLeft: `1px dashed ${Color(theme.palette.primary.main).alpha(0.2).toString()}`,
  },
  bookingInquiryTextDisplayRow: {
    ...common.flexInline,
    ...common.middleText,
  },
  bookingInquiryTextDisplayRowLabel: {
    ...common.textGray700,
    minWidth: 200,
  },
  bookingInquiryTextDisplayRowText: {
    ...common.textGray800,
  },
  customsInstructionSpecialNoteContainer: {
    ...common.textGray700,
    ...common.smallerText,
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: 10,
  },
}));

export const strings = createStrings({
  ja: {
    routeInfo: `${routePlanStrings.route}情報`,
    download: 'ダウンロード',
    editDisabledTitle: '編集不可',
  },
});

export interface BookingInquiryTextDisplayProps {
  label: React.ReactNode;
  text: React.ReactNode;
}

export const BookingInquiryTextDisplay: React.FC<BookingInquiryTextDisplayProps> = (props) => {
  const { label, text } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.bookingInquiryTextDisplayRow}>
      <div className={classes.bookingInquiryTextDisplayRowLabel}>{label}</div>
      <div className={classes.bookingInquiryTextDisplayRowText}>{text}</div>
    </div>
  );
};

export const BookingInquiryDisplay: React.FC<{
  bookingInquiry: BaseInquiryDetail;
  /**
   * Style variant. 'content' to add splitters between cells.
   */
  variant?: 'content';
  cargosDisplayVariant?: BookingInquiryCargosDisplayProps['variant'];
  type?: 'client' | 'admin';
  onOpenForm?: (name: BookingInquiryAdminGroupNames) => void;
  editDisabled?: boolean;
  editDisabledReason?: string;
}> = ({
  bookingInquiry,
  variant,
  cargosDisplayVariant,
  editDisabled,
  editDisabledReason,
  onOpenForm,
  type = 'client',
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const editDisabledAlert = useConfirmDialog();

  const [cargosFolded, setCargosFolded] = useState(true);

  const [localFilesFolded, setLocalFilesFolded] = useState(true);

  const { route_plan, cargos } = bookingInquiry;

  const hasRouteInfo = route_plan && bookingInquiry.route_plan_simulator_result;

  const hasPickups = !!bookingInquiry.cargos.cargos_by_pickup_address;

  const cargosFoldGroupThreshold = 1;
  const cargosFoldThreshold = 8;

  const canFoldCargos =
    (bookingInquiry.cargos.cargos_by_pickup_address &&
      (bookingInquiry.cargos.cargos_by_pickup_address.length > cargosFoldGroupThreshold ||
        bookingInquiry.cargos.cargos_by_pickup_address.some(
          (s) => s.cargos.length > cargosFoldThreshold,
        ))) ||
    (bookingInquiry.cargos.cargos_by_port &&
      bookingInquiry.cargos.cargos_by_port.some((s) => s.cargos.length > cargosFoldThreshold));

  const haveEdit = !!onOpenForm;

  const cargoSummary = React.useMemo(
    () => ({
      unit:
        sumBy(cargos.cargos_by_pickup_address ?? [], (c) => sumBy(c.cargos, (c) => c.num ?? 0)) +
        sumBy(cargos.cargos_by_port ?? [], (c) => sumBy(c.cargos, (c) => c.num ?? 0)),
      kg: bookingInquiry.route_plan_simulator_result?.baggage.total_kg_num ?? 0,
      cbm: bookingInquiry.route_plan_simulator_result?.baggage.total_m3_num ?? 0,
    }),
    [
      cargos.cargos_by_pickup_address,
      cargos.cargos_by_port,
      bookingInquiry.route_plan_simulator_result,
    ],
  );

  const renderTitle = (editName?: BookingInquiryAdminGroupNames, title?: React.ReactNode) => {
    return (
      <div className={classes.bookingInquiryDisplayTitle}>
        {title ? title : editName ? `${bookingInquiryStrings.adminGroupTitle(editName)}情報` : ''}
        {haveEdit && editName && (
          <span
            className={clsx(
              classes.link,
              classes.smallerText,
              editDisabled && classes.textGray700Important,
            )}
            style={{ marginLeft: '1rem', position: 'relative', top: -1 }}
            onClick={(e) => {
              e.stopPropagation();
              !editDisabled
                ? onOpenForm(editName)
                : editDisabledAlert.confirm({
                    title: strings.editDisabledTitle,
                    message: editDisabledReason,
                  });
            }}
          >
            {formStrings.change}
          </span>
        )}
      </div>
    );
  };

  const renderSubtitle = (title: React.ReactNode) => {
    return (
      <div
        className={clsx(classes.smallerText, classes.textPrimary, classes.fontWeight600)}
        style={{ paddingBottom: '0.5rem' }}
      >
        {title}
      </div>
    );
  };

  const renderCargos = () => {
    return (
      <div
        style={{
          gridColumn: 'span 2',
          ...(canFoldCargos && {
            paddingBottom: 0,
          }),
        }}
      >
        {variant === 'content' && <div className={classes.bookingInquiryDisplayGapDivider} />}

        {hasPickups &&
          renderTitle(
            'cargos',
            <>
              {bookingInquiryStrings.collectOption}
              {': '}
              {bookingInquiryStrings.needsPickup}
              {' ('}
              {bookingInquiryStrings.pickupDate}
              {': '}
              {bookingInquiry.pickup_date
                ? dayjs(bookingInquiry.pickup_date).format('YYYY/MM/DD')
                : '-'}
              {')'}
            </>,
          )}
        {!hasPickups &&
          renderTitle(
            'cargos',
            <>
              {' '}
              {bookingInquiryStrings.collectOption}
              {': '}
              {bookingInquiryStrings.noPickup}
              {' ('}
              {bookingInquiryStrings.moveInDate}
              {': '}
              {bookingInquiry.move_in_date
                ? dayjs(bookingInquiry.move_in_date).format('YYYY/MM/DD')
                : '-'}
              {')'}
            </>,
          )}

        <BookingInquiryCargosDisplay
          cargos={bookingInquiry.cargos}
          simulatorResult={bookingInquiry.route_plan_simulator_result ?? undefined}
          groupThreshold={cargosFolded ? cargosFoldGroupThreshold : Infinity}
          cargosThreshold={cargosFolded ? cargosFoldThreshold : Infinity}
          variant={cargosDisplayVariant}
          type={type}
        />

        {canFoldCargos && (
          <div
            className={classes.expandMoreContainer}
            onClick={() => setCargosFolded(!cargosFolded)}
          >
            <ExpandMoreButton expanded={!cargosFolded} />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {editDisabledAlert.renderDialog()}
      <div className={clsx(classes.bookingInquiryDisplayContainer, variant)}>
        <div style={{ gridColumn: 'span 3' }}>
          {renderTitle(
            undefined,
            <div className={classes.flexInline}>
              {strings.routeInfo}
              {route_plan?.service_name && (
                <div className={classes.routePlanCardServiceName}>
                  {routePlanServiceNameStrings[route_plan.service_name]}
                </div>
              )}
            </div>,
          )}
          {hasRouteInfo && (
            <div className={classes.routePlanLineDisplayContainer}>
              <RoutePlanLineDisplay
                routePlan={BookingInquiryHelper.mapToRoutePlan(
                  {
                    route_plan,
                    simulator: bookingInquiry.route_plan_simulator_result!,
                  },
                  bookingInquiry,
                )}
                showDates="all"
              />
            </div>
          )}

          {!hasRouteInfo && '-'}
        </div>

        <div style={{ gridColumn: 'span 1' }}>
          {/** CNEE（輸入者）情報 */}
          {renderTitle('consignee')}
          <BookingInquiryContactDisplay
            contactType="consignee"
            contact={bookingInquiry.consignee}
          />
        </div>

        <div style={{ gridColumn: 'span 2' }}>
          {/** 納品先情報 */}
          {variant === 'content' && <div className={classes.bookingInquiryDisplayGapDivider} />}
          {renderTitle('receiver')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '2.5rem' }}>
            {renderSubtitle(bookingInquiryStrings.receiver)}
            {renderSubtitle(bookingInquiryStrings.preferredArrivalDate)}

            <BookingInquiryContactDisplay
              contactType="receiver"
              contact={bookingInquiry.receiver}
            />
            <div className={clsx(classes.textGray900, classes.smallerText)}>
              {bookingInquiry.preferred_arrival_date || '-'}
            </div>
          </div>
        </div>

        <div style={{ gridColumn: 'span 1' }}>
          {/** SHIPPER（輸出者）情報 */}
          {renderTitle('shipper')}
          <BookingInquiryContactDisplay contactType="shipper" contact={bookingInquiry.shipper} />
        </div>

        {/* 集荷先と荷物の内容 */}
        {renderCargos()}

        <div style={{ gridColumn: 'span 1' }}>
          {/** 贸易条件 */}

          {renderTitle('miscInfo', bookingInquiryStrings.tradeCondition)}
          <div className={clsx(classes.textGray900, classes.smallerText)}>
            {bookingInquiryTradeConditionStrings[bookingInquiry.trade_condition]}
          </div>

          <div style={{ height: 20 }} />

          {renderTitle(undefined, bookingInquiryStrings.tradingAgency)}
          <div className={clsx(classes.textGray900, classes.smallerText)}>
            {tradingAgencyStrings[bookingInquiry.use_trading_agency]}
            {bookingInquiry.use_trading_agency === 'USED' &&
              `/${countryStrings[bookingInquiry.trading_agency_payer]}`}
          </div>

          <div style={{ height: 20 }} />

          {renderTitle(undefined, bookingInquiryStrings.useRcep)}
          <div className={clsx(classes.textGray900, classes.smallerText)}>
            {bookingInquiry.use_rcep ? `${formStrings.use}` : `${formStrings.notUse}`}
          </div>
        </div>

        <div
          style={{
            gridColumn: 'span 2',
          }}
          onClick={() => setLocalFilesFolded(!localFilesFolded)}
        >
          {/** お客様番号、特記事項 */}

          {variant === 'content' && <div className={classes.bookingInquiryDisplayGapDivider} />}

          {renderTitle('customerNo', bookingInquiryStrings.customerNo)}
          <div className={clsx(classes.textGray900, classes.smallerText)}>
            {bookingInquiry.customer_no || '-'}
          </div>

          <div style={{ height: 20 }} />

          {renderTitle('specialNote', bookingInquiryStrings.specialNote)}

          <div className={clsx(classes.textGray700, classes.smallerText, classes.fontWeight600)}>
            {instructionStrings.customsInstructionSpecialNote}
          </div>
          <div className={classes.customsInstructionSpecialNoteContainer}>
            {bookingInquiry.customs_instruction && (
              <>
                {!bookingInquiry.customs_instruction.has_special_note && formStrings.none}
                {bookingInquiry.customs_instruction.has_special_note &&
                  bookingInquiry.customs_instruction.special_note
                    .filter((s) => s.selected === true)
                    .map((item) => (
                      <Fragment key={item.title}>
                        <div>{item.title}</div>
                        {item.note.trim().length > 0 && <div>{item.note.trim()}</div>}
                        {item.note.trim().length <= 0 && <div>-</div>}
                      </Fragment>
                    ))}
              </>
            )}

            {!bookingInquiry.customs_instruction && '-'}
          </div>
          <div style={{ height: 8 }} />

          <div className={clsx(classes.textGray700, classes.smallerText, classes.fontWeight600)}>
            {instructionStrings.deliveryInstructionSpecialNote}
          </div>
          <div className={clsx(classes.textGray700, classes.smallerText)}>
            {bookingInquiry.delivery_instruction && (
              <>
                {!bookingInquiry.delivery_instruction.has_special_note && formStrings.none}
                {bookingInquiry.delivery_instruction.has_special_note &&
                  (bookingInquiry.delivery_instruction.special_note || formStrings.exist)}
              </>
            )}

            {!bookingInquiry.delivery_instruction && '-'}
          </div>
          <div style={{ height: 8 }} />

          <div className={clsx(classes.textGray700, classes.smallerText, classes.fontWeight600)}>
            {bookingInquiryStrings.selfSpecialNote}
          </div>
          <div className={clsx(classes.textGray700, classes.smallerText)}>
            {!bookingInquiry.has_special_note && formStrings.none}
            {bookingInquiry.has_special_note && (bookingInquiry.special_note || formStrings.exist)}
          </div>
        </div>

        {bookingInquiry.route_plan_simulator_result && (
          <div
            style={{
              gridColumn: 'span 3',
            }}
          >
            <PriceSettlement2
              cargoSummary={cargoSummary}
              variant="confirmation"
              tradeCondtion={bookingInquiry.trade_condition}
              simulator={bookingInquiry.route_plan_simulator_result}
            />
          </div>
        )}
      </div>
    </>
  );
};
